import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
	DEFAULT_ANCHORED_PORTAL_GAP,
	DEFAULT_ANCHORED_PORTAL_VIEWPORT_MARGIN,
	getDefaultAnchoredPortalCoords,
} from './positioning';

const getViewport = () => ({
	width: window.innerWidth,
	height: window.innerHeight,
});

const getElementRect = (element) =>
	element?.getBoundingClientRect?.() ?? {
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		width: 0,
		height: 0,
	};

const isAnchorOffscreen = (anchorRect, viewport) =>
	anchorRect.bottom < 0 ||
	anchorRect.top > viewport.height ||
	anchorRect.right < 0 ||
	anchorRect.left > viewport.width;

const getMeasuredElement = (layerElement, measureSelector) =>
	measureSelector ? layerElement?.querySelector(measureSelector) : layerElement;

const AnchoredPortalLayer = ({
	anchorRef,
	children,
	className,
	closeOnEscape = true,
	closeOnOutsidePointerDown = true,
	gap = DEFAULT_ANCHORED_PORTAL_GAP,
	getCoords = getDefaultAnchoredPortalCoords,
	isOpen,
	measureSelector,
	onClose,
	style,
	viewportMargin = DEFAULT_ANCHORED_PORTAL_VIEWPORT_MARGIN,
	...layerProps
}) => {
	const layerRef = useRef(null);
	const [coords, setCoords] = useState({ top: 0, left: 0 });
	const [renderTick, setRenderTick] = useState(0);

	const closeLayer = useCallback(() => {
		onClose?.();
	}, [onClose]);

	const updateCoords = useCallback(() => {
		const anchor = anchorRef?.current;

		if (!anchor) return;

		const anchorRect = getElementRect(anchor);
		const viewport = getViewport();

		if (isAnchorOffscreen(anchorRect, viewport)) {
			closeLayer();
			return;
		}

		const measuredElement = getMeasuredElement(layerRef.current, measureSelector);
		const layerRect = getElementRect(measuredElement);

		setCoords(
			getCoords({
				anchorRect,
				layerRect,
				viewport,
				gap,
				viewportMargin,
			})
		);
	}, [anchorRef, closeLayer, gap, getCoords, measureSelector, viewportMargin]);

	const handlePointerDown = useCallback(
		(event) => {
			if (!closeOnOutsidePointerDown) return;
			if (anchorRef?.current?.contains(event.target)) return;
			if (layerRef.current?.contains(event.target)) return;

			closeLayer();
		},
		[anchorRef, closeLayer, closeOnOutsidePointerDown]
	);

	const handleKeyDown = useCallback(
		(event) => {
			if (closeOnEscape && event.key === 'Escape') closeLayer();
		},
		[closeLayer, closeOnEscape]
	);

	useLayoutEffect(() => {
		if (!isOpen) return;

		if (!anchorRef?.current) {
			setRenderTick((currentTick) => currentTick + 1);
			return;
		}

		updateCoords();
	}, [anchorRef, isOpen, renderTick, updateCoords]);

	useEffect(() => {
		if (!isOpen) return undefined;

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		window.addEventListener('scroll', updateCoords, true);
		window.addEventListener('resize', updateCoords);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('scroll', updateCoords, true);
			window.removeEventListener('resize', updateCoords);
		};
	}, [handleKeyDown, handlePointerDown, isOpen, updateCoords]);

	if (!isOpen || !anchorRef?.current) return null;

	return createPortal(
		<div
			ref={layerRef}
			className={className}
			style={{ position: 'fixed', ...coords, ...style }}
			{...layerProps}
		>
			{children}
		</div>,
		document.body
	);
};

export default AnchoredPortalLayer;
